import type { SearchResult } from '@monitor/contracts';
import { AppWindow, Bell, Bug, CornerDownLeft, GitBranch, PlugZap, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useSearch } from '../api/hook.js';
import { NAV_ITEM } from './navigation.js';

const KIND_ICON: Record<SearchResult['kind'], ReactNode> = {
  application: <AppWindow size={16} />,
  error: <Bug size={16} />,
  correlation: <GitBranch size={16} />,
  provider: <PlugZap size={16} />,
  alert: <Bell size={16} />,
  page: <CornerDownLeft size={16} />,
};

const KIND_LABEL: Record<SearchResult['kind'], string> = {
  page: 'Vai a',
  application: 'Application',
  error: 'Error',
  correlation: 'CorrelationId',
  provider: 'Provider',
  alert: 'Alert',
};

/** Ctrl+K: jump to any page, Application, Error, Provider, Alert or CorrelationId. */
export function CommandPalette({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const remote = useSearch(text);

  const items = useMemo(() => {
    const query = text.toLowerCase().trim();
    const page: SearchResult[] = NAV_ITEM.filter((item) => !query || `${item.label} ${item.hint}`.toLowerCase().includes(query)).map((item) => ({
      kind: 'page',
      id: item.path,
      label: item.label,
      detail: item.hint,
      href: item.path,
    }));
    const looksLikeCorrelation = /^cor_[0-9a-f]{6,}$/i.test(text.trim());
    const direct: SearchResult[] = looksLikeCorrelation ? [{ kind: 'correlation', id: text.trim(), label: text.trim(), detail: 'Apri la sequenza completa nel Log', href: `/log?correlationId=${text.trim()}` }] : [];
    return [...direct, ...page, ...(remote.data ?? []).filter((item) => !direct.some((d) => d.id === item.id))];
  }, [text, remote.data]);

  useEffect(() => setCursor(0), [text]);

  const go = (item: SearchResult | undefined) => {
    if (!item) return;
    navigate(item.href);
    onClose();
  };

  const onKey = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((value) => Math.min(items.length - 1, value + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((value) => Math.max(0, value - 1));
    } else if (event.key === 'Enter') {
      go(items[cursor]);
    } else if (event.key === 'Escape') onClose();
  };

  let lastKind: string | null = null;
  return createPortal(
    <div className="backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog cmdk" role="dialog" aria-modal="true" aria-label="Cerca">
        <div className="cmdk-input">
          <Search size={18} className="muted" />
          <input autoFocus value={text} onChange={(event) => setText(event.target.value)} onKeyDown={onKey} placeholder="Cerca Application, Error, Provider, CorrelationId…" aria-label="Cerca" />
          <kbd>Esc</kbd>
        </div>
        <div className="cmdk-list" role="listbox">
          {items.length === 0 ? <div className="empty">Nessun risultato per “{text}”</div> : null}
          {items.map((item, index) => {
            const header = item.kind !== lastKind ? <div className="cmdk-group">{KIND_LABEL[item.kind]}</div> : null;
            lastKind = item.kind;
            return (
              <div key={`${item.kind}:${item.id}`}>
                {header}
                <button type="button" role="option" aria-selected={index === cursor} className={`cmdk-item${index === cursor ? ' on' : ''}`} onMouseEnter={() => setCursor(index)} onClick={() => go(item)}>
                  {KIND_ICON[item.kind]}
                  <span className="cmdk-item-text">
                    <span className={item.kind === 'correlation' ? 'mono' : ''}>{item.label}</span>
                    <small>{item.detail}</small>
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
